import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default vote settings
const DEFAULT_VOTE_SETTINGS = {
  vote_emoji_up: '🐾',
  vote_emoji_down: '❌',
  vote_theme: 'dogs',
  show_elo_temperature: true,
};

const ClubSettingsContext = createContext({
  voteSettings: DEFAULT_VOTE_SETTINGS,
  loading: false,
  clubId: null,
});

export const useClubSettings = () => useContext(ClubSettingsContext);

export const ClubSettingsProvider = ({ children, clubId = 'default' }) => {
  const [voteSettings, setVoteSettings] = useState(DEFAULT_VOTE_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [currentClubId, setCurrentClubId] = useState(clubId);

  useEffect(() => {
    const fetchClubSettings = async () => {
      setLoading(true);
      try {
        // Fetch clubs list to get the current club's settings
        const res = await fetch(`${API_URL}/api/clubs/`);
        if (res.ok) {
          const clubs = await res.json();
          // Find the club by ID or use the first one (default/flagship club)
          let club = null;
          if (clubId && clubId !== 'default') {
            club = clubs.find(c => c.id === clubId || c.slug === clubId);
          }
          if (!club && clubs.length > 0) {
            // Use the flagship/featured club or first one
            club = clubs.find(c => c.is_featured) || clubs[0];
          }
          
          if (club) {
            setVoteSettings({
              vote_emoji_up: club.vote_emoji_up || DEFAULT_VOTE_SETTINGS.vote_emoji_up,
              vote_emoji_down: club.vote_emoji_down || DEFAULT_VOTE_SETTINGS.vote_emoji_down,
              vote_theme: club.vote_theme || DEFAULT_VOTE_SETTINGS.vote_theme,
              show_elo_temperature: club.show_elo_temperature ?? DEFAULT_VOTE_SETTINGS.show_elo_temperature,
            });
            setCurrentClubId(club.id);
          }
        }
      } catch (error) {
        console.error('Failed to fetch club settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchClubSettings();
  }, [clubId]);

  const value = {
    voteSettings,
    loading,
    clubId: currentClubId,
    // Helper to update settings (used by VoteEmojiSettings component)
    updateVoteSettings: (newSettings) => {
      setVoteSettings(prev => ({ ...prev, ...newSettings }));
    },
  };

  return (
    <ClubSettingsContext.Provider value={value}>
      {children}
    </ClubSettingsContext.Provider>
  );
};

export default ClubSettingsContext;
